/**
 * Typed localStorage wrapper. The legacy code scatters 20+ keys with inconsistent
 * naming and types; this module is the single source of truth.
 *
 * Reads return `null` for missing keys. Writes coerce to string or remove the key.
 * Numeric/boolean values are encoded as strings — never `JSON.stringify` primitives.
 */

const Keys = /** @type {const} */ ({
  userId: 'userId',
  phone: 'phone',
  plate: 'plate',
  carYear: 'carYear',
  isLoggedIn: 'isLoggedIn',
  termsAccepted: 'termsAccepted',
  lastLoginAt: 'lastLoginAt',
  /** Updated by `validateSession` after a successful server-side check, so
   *  the next cold start can short-circuit the re-validation if the cached
   *  result is still fresh. */
  lastValidatedAt: 'lastValidatedAt',
  fcmToken: 'fcmToken',
  deviceId: 'deviceId',
  auth: 'auth',
  boundOwnerId: 'boundOwnerId',
  lastSearch: 'c2c_lastSearch',
  searchHistory: 'c2c_searchHistory',
  lastChat: 'c2c_lastChat',
  /** Admin-granted flag that lets a user search without a registered vehicle.
   *  Synced from `users/{uid}/searchBypass` on auth and via live listener.
   *  Cached locally so the gate works instantly on cold start before RTDB
   *  fetch completes — the RTDB listener overwrites it shortly after. */
  searchBypass: 'searchBypass',
});

/**
 * Keys preserved across `clearAll()`. These represent **physical-device** or
 * **previously-verified-identity** state that should survive a logout (even a
 * hard, forced one) so the user doesn't have to redo SMS verification on the
 * very same phone they already proved they own.
 *
 *   - `deviceId`       — random per-install identifier; the basis of trust.
 *   - `phone`          — auto-fill the login form + key the trustedDevices lookup.
 *   - `termsAccepted`  — the user already accepted; don't re-prompt.
 */
const KEEP_KEYS = /** @type {const} */ ([
  Keys.deviceId,
  Keys.phone,
  Keys.termsAccepted,
]);

/**
 * @param {string} key
 * @returns {string | null}
 */
function read(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * @param {string} key
 * @param {string | null | undefined} value
 */
function write(key, value) {
  try {
    if (value == null || value === '') localStorage.removeItem(key);
    else localStorage.setItem(key, String(value));
  } catch {
    // localStorage may be disabled (incognito) — fail silently rather than crashing
  }
}

/**
 * @param {string} key
 * @returns {boolean}
 */
function readBool(key) {
  return read(key) === 'true';
}

/**
 * @param {string} key
 * @returns {unknown}
 */
function readJson(key) {
  const raw = read(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * @param {string} key
 * @param {unknown} value
 */
function writeJson(key, value) {
  if (value == null) write(key, null);
  else write(key, JSON.stringify(value));
}

export const LocalState = {
  // -------- identity --------
  get userId() { return read(Keys.userId); },
  set userId(v) { write(Keys.userId, v); },

  get phone() { return read(Keys.phone); },
  set phone(v) { write(Keys.phone, v); },

  get plate() { return read(Keys.plate); },
  set plate(v) { write(Keys.plate, v); },

  get carYear() { return read(Keys.carYear); },
  set carYear(v) { write(Keys.carYear, v); },

  get deviceId() { return read(Keys.deviceId); },
  set deviceId(v) { write(Keys.deviceId, v); },

  // -------- session flags --------
  get isLoggedIn() { return readBool(Keys.isLoggedIn); },
  set isLoggedIn(v) { write(Keys.isLoggedIn, v ? 'true' : null); },

  get termsAccepted() { return readBool(Keys.termsAccepted); },
  set termsAccepted(v) { write(Keys.termsAccepted, v ? 'true' : null); },

  get searchBypass() { return readBool(Keys.searchBypass); },
  set searchBypass(v) { write(Keys.searchBypass, v ? 'true' : null); },

  get lastLoginAt() {
    const v = read(Keys.lastLoginAt);
    return v ? Number(v) : null;
  },
  set lastLoginAt(/** @type {number | null} */ v) {
    write(Keys.lastLoginAt, v ? String(v) : null);
  },

  get lastValidatedAt() {
    const v = read(Keys.lastValidatedAt);
    return v ? Number(v) : null;
  },
  set lastValidatedAt(/** @type {number | null} */ v) {
    write(Keys.lastValidatedAt, v ? String(v) : null);
  },

  // -------- push --------
  get fcmToken() { return read(Keys.fcmToken); },
  set fcmToken(v) { write(Keys.fcmToken, v); },

  // -------- search & chat caches --------
  get lastSearch() { return readJson(Keys.lastSearch); },
  set lastSearch(v) { writeJson(Keys.lastSearch, v); },

  get searchHistory() {
    const v = readJson(Keys.searchHistory);
    return Array.isArray(v) ? v : [];
  },
  set searchHistory(v) { writeJson(Keys.searchHistory, v); },

  get lastChat() { return readJson(Keys.lastChat); },
  set lastChat(v) { writeJson(Keys.lastChat, v); },

  // -------- bulk operations --------
  /** Clear identity + session data. Use on logout. */
  clearSession() {
    [
      Keys.userId, Keys.isLoggedIn, Keys.lastLoginAt,
      Keys.auth, Keys.boundOwnerId, Keys.termsAccepted,
      Keys.searchBypass,
    ].forEach((k) => write(k, null));
  },

  /**
   * Clear all session/identity state EXCEPT the keys listed in `KEEP_KEYS`
   * (deviceId, phone, termsAccepted). Used on logout / forced logout so the
   * physical device remains recognised on next sign-in and the user isn't
   * forced through SMS OTP again.
   */
  clearAll() {
    const keep = new Set(KEEP_KEYS);
    Object.values(Keys).forEach((k) => {
      if (!keep.has(/** @type {any} */ (k))) write(k, null);
    });
  },

  /**
   * Wipe absolutely everything, including the device identity and verified
   * phone. Equivalent to "reinstall the app". Use only for explicit
   * "delete account" / "reset device" flows. NOT called by any current
   * logout path — those use `clearAll` (which preserves deviceId/phone).
   */
  nukeEverything() {
    Object.values(Keys).forEach((k) => write(k, null));
  },
};

export { Keys };
